import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { defineStore } from 'pinia';

import { AlertMode, IAlertMessage } from '@/types/components';

export const useAlertStore = defineStore('alert', () => {
  const { t } = useI18n();

  const messages = ref<IAlertMessage>({
    title: '',
    message: '',
    bodyAccentTitle: '',
    bodyTitle: '',
    subMessage: '',
  });
  const visible = ref<boolean>(false);
  const clickSelfToClose = ref<boolean>(false);
  const alertMode = ref<AlertMode>('default');
  const confirmAction = ref<void>(() => {});
  const cancelAction = ref<void>(() => {});
  const confirmButtonName = ref(t('confirm')); // 확인 버튼 문구
  const cancelButtonName = ref('button_cancel'); // 확인 버튼 문구

  // 확인 버튼 문구를 i18n으로 관리
  const textButtonConfirm = computed(() => ({
    confirm: t('confirm'),
    titleUnknownError: t('guide'),
    messageUnknownErrorStart: t('alert_message_unknown_error_start'),
    messageUnknownErrorEnd: t('alert_message_unknown_error_end'),
  }));

  const confirm = (
    confirmMessages,
    actions,
    selfToClose = true,
    buttonName = textButtonConfirm.value.confirm,
    mode: AlertMode = 'default'
  ) => {
    messages.value = confirmMessages;
    confirmAction.value = actions.confirm;
    cancelAction.value = actions.cancel ? actions.cancel : () => {};
    clickSelfToClose.value = selfToClose;
    visible.value = true;
    confirmButtonName.value = buttonName;
    alertMode.value = mode;
  };

  const alert = (
    alertMessages,
    actions = { confirm: () => close() }, // 기본값 설정
    selfToClose = false,
    buttonName = textButtonConfirm.value.confirm // 기본값 설정
  ) => {
    messages.value = alertMessages;
    confirmAction.value = actions && actions.confirm ? actions.confirm : () => close();
    cancelAction.value = null;
    clickSelfToClose.value = selfToClose;
    visible.value = true;
    confirmButtonName.value = buttonName;
  };

  const close = () => {
    visible.value = false;
  };

  const alertNetworkError = (error) => {
    const code = error.message === 'Network Error' ? '9000' : '9001';
    console.error('NETWORK ERROR:', error);
    alert(
      {
        title: t('guide'),
        message: `${t('error_server_or_network')}<br>` + `${t('retry_after_few_minutes')} [${code}]`,
      },
      { confirm: () => {} },
      false
    );
  };
  const alertAuthenticationExpired = (redirectToLogin) => {
    alert(
      {
        title: t('authentication_expired_title'),
        message: t('authentication_expired_message'),
      },
      { confirm: () => redirectToLogin },
      false
    );
  };

  const unknownErrorAlert = (confirm = () => close(), buttonName = textButtonConfirm.value.confirm) => {
    alert(
      {
        title: textButtonConfirm.value.titleUnknownError,
        message:
          textButtonConfirm.value.messageUnknownErrorStart + '<br>' + textButtonConfirm.value.messageUnknownErrorEnd,
      },
      { confirm: confirm },
      false,
      buttonName
    );
  };

  watch(visible, (newVal) => {
    if (!newVal) {
      cancelButtonName.value = 'button_cancel';
    }
  });

  return {
    confirm,
    alert,
    close,
    unknownErrorAlert,
    alertNetworkError,
    alertAuthenticationExpired,
    alertMode,
    visible,
    messages,
    confirmAction,
    cancelAction,
    clickSelfToClose,
    confirmButtonName,
    cancelButtonName,
  };
});
